import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, increment } from 'firebase/firestore';
import {
  QUESTIONS,
  DEMO_QUESTIONS,
  computeResonance,
  pickObservations,
  getSignalStrength,
} from '../data/alignmentData';

// ── Animations ────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-8px); }
`;

const softPulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50%       { opacity: 0.9; }
`;

// ── Layout ────────────────────────────────────────────────────────────

const Wrapper = styled.section`
  background: #07000e;
  border: 1px solid #1a0028;
  border-radius: 12px;
  padding: 36px 28px 32px;
  margin: 24px auto;
  width: 100%;
  max-width: 620px;
  box-sizing: border-box;
  font-family: 'Creepster', Arial, sans-serif;
  color: #c4b8d8;
`;

const Fade = styled.div`
  animation: ${p => p.$out ? fadeOut : fadeUp} 0.22s ease both;
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #220035, transparent);
  margin: 26px 0;
`;

const SmallMono = styled.div`
  font-family: monospace;
  font-size: 0.65rem;
  color: #3a1a55;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 20px;
`;

// ── Intro ─────────────────────────────────────────────────────────────

const IntroTitle = styled.h2`
  font-size: 1.9rem;
  color: #9955cc;
  margin: 0 0 10px;
  text-align: center;
  letter-spacing: 0.5px;
`;

const IntroSub = styled.p`
  text-align: center;
  color: #5a3a75;
  font-size: 0.88rem;
  margin: 0 0 32px;
  line-height: 1.6;
  font-style: italic;
`;

const BeginBtn = styled.button`
  display: block;
  margin: 0 auto;
  background: transparent;
  color: #9955cc;
  border: 1px solid #4a1a77;
  border-radius: 8px;
  padding: 12px 40px;
  font-size: 1.1rem;
  cursor: pointer;
  font-family: 'Creepster', Arial, sans-serif;
  letter-spacing: 2px;
  transition: all 0.2s;
  &:hover {
    border-color: #cc44ff;
    color: #cc44ff;
    background: rgba(150, 50, 220, 0.06);
  }
`;

// ── Questions ─────────────────────────────────────────────────────────

const QuestionNum = styled.div`
  font-family: monospace;
  font-size: 0.62rem;
  color: #3a1a55;
  letter-spacing: 1px;
  margin-bottom: 18px;
`;

const QuestionText = styled.h3`
  font-size: 1.25rem;
  color: #e4d8f8;
  margin: 0 0 22px;
  line-height: 1.4;
  font-weight: normal;
`;

const AnswerBtn = styled.button`
  display: block;
  width: 100%;
  background: transparent;
  border: 1px solid #160025;
  border-radius: 6px;
  color: #8a7aaa;
  padding: 11px 16px;
  margin-bottom: 8px;
  text-align: left;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1.35;

  &:hover {
    background: rgba(100, 30, 160, 0.08);
    border-color: #6633aa;
    color: #ddd;
    padding-left: 20px;
  }
  &:last-child { margin-bottom: 0; }
`;

const DemoAnswerBtn = styled(AnswerBtn)`
  text-align: center;
  font-size: 1rem;
`;

// ── Reading state ─────────────────────────────────────────────────────

const ReadingText = styled.div`
  text-align: center;
  color: #4a2a66;
  font-size: 0.8rem;
  font-family: monospace;
  letter-spacing: 2px;
  padding: 60px 0;
  animation: ${softPulse} 1.4s ease-in-out infinite;
`;

// ── Results ───────────────────────────────────────────────────────────

const ObsBlock = styled.div`
  animation: ${fadeUp} 0.5s ease ${p => (p.$i || 0) * 160 + 100}ms both;
  margin-bottom: 24px;
  padding-left: 14px;
  border-left: 1px solid #2a0040;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const ObsText = styled.p`
  margin: 0;
  font-size: 1.02rem;
  color: #c0b0d8;
  line-height: 1.6;
`;

const SignalLine = styled.div`
  font-family: monospace;
  font-size: 0.65rem;
  color: #3a1a55;
  letter-spacing: 2px;
  text-align: right;
  animation: ${fadeUp} 0.5s ease 0.9s both;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  animation: ${fadeUp} 0.5s ease 1.1s both;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const ShareBtn = styled.button`
  background: transparent;
  color: #7744aa;
  border: 1px solid #2e0045;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 0.85rem;
  cursor: pointer;
  font-family: 'Creepster', Arial, sans-serif;
  letter-spacing: 0.5px;
  transition: all 0.18s;
  &:hover { border-color: #9933cc; color: #cc44ff; }
`;

const Credit = styled.div`
  text-align: center;
  color: #2a1040;
  font-size: 0.7rem;
  font-style: italic;
  margin-top: 28px;
  animation: ${fadeUp} 0.5s ease 1.3s both;
  opacity: 0;
  animation-fill-mode: forwards;
`;

// ── Component ─────────────────────────────────────────────────────────

const STATS_REF = doc(db, 'abelAlignmentEngine', 'stats');

export default function AbelAlignment() {
  const [phase, setPhase]       = useState('intro');
  const [demoStep, setDemoStep] = useState(0);
  const [qIndex, setQIndex]     = useState(0);
  const [traits, setTraits]     = useState({});
  const [demos, setDemos]       = useState({});
  const [animOut, setAnimOut]   = useState(false);

  const [observations, setObservations] = useState([]);
  const [signal, setSignal]             = useState('');
  const [copied, setCopied]             = useState(false);

  // ── Answer handlers ────────────────────────────────────────────────

  const handleDemoAnswer = useCallback(function(key) {
    var field = DEMO_QUESTIONS[demoStep].id;
    setDemos(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = key;
      return next;
    });
    if (demoStep < DEMO_QUESTIONS.length - 1) {
      setDemoStep(function(s) { return s + 1; });
    } else {
      setPhase('questions');
    }
  }, [demoStep]);

  const finalize = useCallback(function(finalTraits, finalDemos) {
    var score  = computeResonance(finalTraits);
    var obs    = pickObservations(finalTraits, 4);
    var sig    = getSignalStrength(score);

    setObservations(obs);
    setSignal(sig);
    setPhase('reading');

    // Brief pause before revealing results
    setTimeout(function() { setPhase('results'); }, 1800);

    // Persist aggregate stats only — no individual data stored
    getDoc(STATS_REF).then(function(snap) {
      var updates = {};
      updates['total'] = increment(1);
      updates['signalStrength.' + sig.replace(/\s/g, '_')] = increment(1);
      if (finalDemos && finalDemos.entity) {
        updates['entityBreakdown.' + finalDemos.entity] = increment(1);
      }
      if (finalDemos && finalDemos.arc) {
        updates['arcBreakdown.' + finalDemos.arc] = increment(1);
      }
      if (snap.exists()) {
        return updateDoc(STATS_REF, updates);
      } else {
        return setDoc(STATS_REF, { total: 1 });
      }
    }).catch(function() {});
  }, []);

  const handleAnswer = useCallback(function(answer) {
    if (animOut) return;

    var newTraits = Object.assign({}, traits);
    Object.keys(answer.traits).forEach(function(t) {
      newTraits[t] = (newTraits[t] || 0) + answer.traits[t];
    });

    if (qIndex < QUESTIONS.length - 1) {
      setAnimOut(true);
      setTimeout(function() {
        setTraits(newTraits);
        setQIndex(function(i) { return i + 1; });
        setAnimOut(false);
      }, 200);
    } else {
      setTraits(newTraits);
      finalize(newTraits, demos);
    }
  }, [animOut, traits, qIndex, finalize, demos]);

  const handleShare = useCallback(function() {
    var lines = observations.map(function(o) { return '— ' + o; }).join('\n');
    var text = 'signal: ' + signal + '\n\n' + lines + '\n\nabeliciousness.com';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(function() {
        setCopied(true);
        setTimeout(function() { setCopied(false); }, 2000);
      }).catch(function() {});
    }
  }, [observations, signal]);

  const handleRetake = useCallback(function() {
    setPhase('intro');
    setDemoStep(0);
    setQIndex(0);
    setTraits({});
    setDemos({});
    setObservations([]);
    setSignal('');
    setAnimOut(false);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────

  if (phase === 'intro') {
    return (
      <Wrapper>
        <IntroTitle>frequency check</IntroTitle>
        <IntroSub>
          ten questions.<br />
          no login. no profile. nothing stored.<br />
          just a signal read.
        </IntroSub>
        <BeginBtn onClick={() => setPhase('demo')}>begin</BeginBtn>
      </Wrapper>
    );
  }

  if (phase === 'demo') {
    var dq = DEMO_QUESTIONS[demoStep];
    return (
      <Wrapper>
        <Fade $out={false}>
          <QuestionNum>{demoStep + 1} / 2 — calibration</QuestionNum>
          <QuestionText>{dq.question}</QuestionText>
          {dq.answers.map(function(a) {
            return (
              <DemoAnswerBtn key={a.key} onClick={() => handleDemoAnswer(a.key)}>
                {a.text}
              </DemoAnswerBtn>
            );
          })}
        </Fade>
      </Wrapper>
    );
  }

  if (phase === 'questions') {
    var q = QUESTIONS[qIndex];
    return (
      <Wrapper>
        <Fade $out={animOut} key={qIndex}>
          <QuestionNum>{qIndex + 1} / {QUESTIONS.length}</QuestionNum>
          <QuestionText>{q.question}</QuestionText>
          {q.answers.map(function(a, i) {
            return (
              <AnswerBtn key={i} onClick={() => handleAnswer(a)}>
                {a.text}
              </AnswerBtn>
            );
          })}
        </Fade>
      </Wrapper>
    );
  }

  if (phase === 'reading') {
    return (
      <Wrapper>
        <ReadingText>reading signal...</ReadingText>
      </Wrapper>
    );
  }

  if (phase === 'results') {
    return (
      <Wrapper>
        <SmallMono>signal read complete</SmallMono>

        {observations.map(function(obs, i) {
          return (
            <ObsBlock key={i} $i={i}>
              <ObsText>{obs}</ObsText>
            </ObsBlock>
          );
        })}

        <Divider />

        <SignalLine>signal: {signal}</SignalLine>

        <ActionRow>
          <ShareBtn onClick={handleShare}>
            {copied ? 'copied' : 'copy result'}
          </ShareBtn>
          <ShareBtn onClick={handleRetake}>retake</ShareBtn>
        </ActionRow>

        <Credit>built somewhere in LA by Abel</Credit>
      </Wrapper>
    );
  }

  return null;
}
